import { noteResponse, noteNetworkFailure } from './pwa/net.js';

export class ApiError extends Error {
  constructor(
    public status: number,
    message: string,
  ) {
    super(message);
  }
}

let onUnauthorized: () => void = () => undefined;
export function setUnauthorizedHandler(fn: () => void): void {
  onUnauthorized = fn;
}

/* V8 §11: "The job list, the diff and the artefacts must load, and a
 * failure to load must be retried automatically before it is shown as an
 * error." A GET is idempotent, so a transport failure or a 5xx is retried
 * twice with a widening gap before anything reaches a screen. Only then is
 * it an error the reader has to read. Writes are never retried here: they
 * carry an action id and are replayed deliberately, not silently. */
const GET_RETRIES = 2;
const RETRY_GAP_MS = [400, 1200];

async function call<T>(method: string, path: string, body?: unknown): Promise<T> {
  if (method !== 'GET') return callOnce<T>(method, path, body);
  let last: unknown;
  for (let attempt = 0; attempt <= GET_RETRIES; attempt++) {
    try {
      return await callOnce<T>(method, path, body);
    } catch (err) {
      last = err;
      const status = err instanceof ApiError ? err.status : -1;
      // A refusal is an answer: 401, 403 and 404 are not retried.
      const worthRetrying = status === 0 || status >= 500 || status === 429;
      if (!worthRetrying || attempt === GET_RETRIES) break;
      await new Promise((r) => setTimeout(r, RETRY_GAP_MS[attempt] ?? 1200));
    }
  }
  throw last;
}

async function callOnce<T>(method: string, path: string, body?: unknown): Promise<T> {
  let res: Response;
  try {
    res = await fetch(path, {
      method,
      headers: {
        ...(body !== undefined ? { 'content-type': 'application/json' } : {}),
        ...(method !== 'GET' ? { 'x-ops-app': '1' } : {}),
      },
      body: body === undefined ? undefined : JSON.stringify(body),
      credentials: 'same-origin',
    });
  } catch (err) {
    noteNetworkFailure();
    /* Keep what the transport actually said (V6 section J). Flattening a
       DNS failure, a reset tunnel, a CORS refusal and an abort into one
       string called "No signal." is how a real problem became invisible
       and, worse, indistinguishable from being offline. */
    const detail = err instanceof Error && err.message ? ` (${err.message})` : '';
    throw new ApiError(0, `The box did not answer${detail}.`);
  }
  noteResponse(res);
  if (res.status === 401) {
    onUnauthorized();
    throw new ApiError(401, 'Signed out.');
  }
  const text = await res.text();
  let parsed: unknown = null;
  let parseFailed = false;
  try {
    parsed = text ? JSON.parse(text) : null;
  } catch {
    parsed = null;
    parseFailed = true;
  }
  if (!res.ok) {
    const message =
      (parsed as { error?: string })?.error ??
      (parseFailed && text ? `${text.slice(0, 140).trim()} (${res.status})` : `Request failed (${res.status}).`);
    throw new ApiError(res.status, message);
  }
  /* A 200 carrying something that is not JSON used to be returned as
     `null as T`, which reached a screen as a TypeError inside a `.then`
     rather than as an error anybody catches. That is how a proxy's HTML
     error page or a stale cached shell became a blank screen. */
  if (parseFailed) {
    throw new ApiError(res.status, 'The box answered with something this app could not read.');
  }
  return parsed as T;
}

export const api = {
  get: <T>(path: string) => call<T>('GET', path),
  post: <T>(path: string, body?: unknown) => call<T>('POST', path, body),
  put: <T>(path: string, body?: unknown) => call<T>('PUT', path, body),
  patch: <T>(path: string, body?: unknown) => call<T>('PATCH', path, body),
  del: <T>(path: string) => call<T>('DELETE', path),
  // Multipart: files go up as FormData, everything else identical.
  upload: async <T>(path: string, files: File[]): Promise<T> => {
    const form = new FormData();
    files.forEach((f, i) => form.append(`file${i}`, f, f.name));
    let res: Response;
    try {
      res = await fetch(path, {
        method: 'POST',
        headers: { 'x-ops-app': '1' },
        body: form,
        credentials: 'same-origin',
      });
    } catch {
      noteNetworkFailure();
      throw new ApiError(0, 'No signal.');
    }
    noteResponse(res);
    if (res.status === 401) {
      onUnauthorized();
      throw new ApiError(401, 'Signed out.');
    }
    const parsed = (await res.json().catch(() => null)) as { error?: string } | null;
    if (!res.ok) throw new ApiError(res.status, parsed?.error ?? `Upload failed (${res.status}).`);
    return parsed as T;
  },
};

// ── Shared shapes (Ops V7: facts, attempts, endings, questions) ─────────────
export type JobState = 'scheduled' | 'queued' | 'running' | 'ended' | 'closed';
export type EndingKind = 'done' | 'needs_input' | 'limit' | 'auth' | 'api_error' | 'crashed' | 'stopped' | 'budget';
export type ActionKind =
  | 'continue'
  | 'continue_other'
  | 'continue_with_red'
  | 'retry'
  | 'stop'
  | 'stop_command'
  | 'answer'
  | 'message'
  | 'ship'
  | 'cancel'
  | 'set_budget'
  | 'set_auto_continue'
  | 'set_auto_switch_model'
  | 'mode';

export interface Ending {
  kind: EndingKind;
  detail: string;
  resetAt?: string | null;
  status?: number | null;
  exitCode?: number | null;
  signal?: string | null;
  raw?: string;
  at: string;
}

export interface Question {
  id: string;
  kind: 'question' | 'permission';
  text: string;
  options: string[];
  input: unknown;
  askedAt: string;
}

export interface Attempt {
  id: string;
  n: number;
  accountId: string;
  accountLabel: string;
  sessionId: string | null;
  resumed: boolean;
  unit: string | null;
  model: string | null;
  /** The full model id the session was launched with. */
  cliModel: string | null;
  /** What the session named in its own init line. */
  reportedModel: string | null;
  /** What the API answered the main thread with. */
  answeredModel: string | null;
  /** False when the session's own report disagrees with the launch. */
  modelMatch: boolean | null;
  effort: string | null;
  startedAt: string;
  endedAt: string | null;
  exitCode: number | null;
  signal: string | null;
  ending: Ending | null;
  alive: boolean;
  lastEventAt: string | null;
  costUsd: number | null;
}

export interface DeployFacts {
  kind: 'pages' | 'staging';
  runId: number | null;
  status: string | null;
  conclusion: string | null;
  url: string | null;
  liveUrl: string | null;
  verifiedSha?: string | null;
  refreshedAt: string | null;
}

export interface Facts {
  processAlive: boolean;
  pid: number | null;
  attempt: number;
  account: string | null;
  accountLabel: string;
  lastEventAt: string | null;
  openTool: { name: string; detail?: string; since: string } | null;
  activity: JobActivity | null;
  lastCommitAt: string | null;
  lastCommitSha: string | null;
  unpushedCommits: number;
  branchPushed: boolean;
  dirty: boolean;
  worktree: string | null;
  lastSnapshotAt: string | null;
  snapshotError?: { at: string; message: string } | null;
  snapshotRef: string | null;
  snapshotRestore: string | null;
  prNumber: number | null;
  prUrl: string | null;
  headSha: string | null;
  ciConclusion: 'success' | 'failure' | 'pending' | null;
  ciRefreshedAt: string | null;
  /** ci-fix: the lane that ran, the digest for a red, and the push gate. */
  ciLane?: string | null;
  ciDigestUrl?: string | null;
  pushPointAt?: string | null;
  pushPointWhy?: string | null;
  preflight?: { ok: boolean; summary: string; at: string; strikes: number } | null;
  merged: boolean;
  mergeSha: string | null;
  deploy: DeployFacts | null;
  budgetUsd: number | null;
  budgetHours: number | null;
  costUsd: number | null;
  shots: { unique: number; duplicates: number };
  /** V8 §11: a message written down and not yet echoed back by the CLI. */
  messagePending?: boolean;
}

export interface Job {
  id: string;
  title: string;
  prompt: string;
  mode: 'hold' | 'ship';
  kind: 'change' | 'workshop';
  repo: string;
  referenceRepo: string | null;
  model: string;
  effort: string | null;
  state: JobState;
  phase: string;
  phaseDetail: string | null;
  closedReason: string | null;
  ending: Ending | null;
  question: Question | null;
  facts: Facts;
  buttons: string[];
  attempt: Attempt | null;
  attempts: Attempt[];
  accountId: string | null;
  autoContinueOtherAccount: boolean;
  budgetUsd: number | null;
  budgetHours: number | null;
  baseBranch: string;
  branch: string | null;
  slug: string | null;
  prNumber: number | null;
  prUrl: string | null;
  headSha: string | null;
  approvedSha: string | null;
  mergeCommitSha: string | null;
  costUsd: number | null;
  turns: number | null;
  createdAt: string;
  startedAt: string | null;
  finishedAt: string | null;
  updatedAt: string;
  scheduledFor: string | null;
  queuePosition: number | null;
  lastCommitAt: string | null;
  lastPushAt: string | null;
  // ── V8 ──
  /** The state the daemon DRIVES. `phase` is a derived word; this is owned. */
  lifecycle: Lifecycle;
  lifecycleLabel: string;
  lifecycleDetail: string | null;
  lifecycleAt: string | null;
  live: boolean;
  breakers: Breakers;
  parkedUntil: string | null;
  parkedReason: string | null;
  slot: number | null;
  pinnedAccount: boolean;
  pinnedModel: boolean;
  fallbackModel: string | null;
  /** A limit may move the job to the other model. Off unless ticked. */
  autoSwitchModel: boolean;
  modelLabel: string;
  modelCheck: ModelCheck;
  prDraft: boolean;
  /** When the cost figure was last measured. Null means never. */
  costAsOf: string | null;
}

export type Lifecycle =
  | 'queued' | 'working' | 'pushed' | 'pr_open' | 'ci_running' | 'ci_red' | 'fixing'
  | 'ci_green' | 'merge_queued' | 'merged' | 'candidate' | 'staging_verified' | 'promotable' | 'live'
  | 'blocked_on_human' | 'budget_exhausted' | 'cycles_exhausted' | 'abandoned_by_operator'
  | 'parked_limit' | 'parked_capacity';

export interface Breaker {
  used: number;
  limit: number;
  warnedAt?: string | null;
}
export interface Breakers {
  fixCycles: Breaker;
  spendUsd: Breaker;
  wallHours: Breaker;
  restarts: Breaker;
}

/** What the Finisher decided about one failing CI job, and why (§4). */
export interface Verdict {
  id: string;
  cycle: number;
  headSha: string;
  jobName: string;
  fingerprint: string;
  verdict: 'flake' | 'infrastructure' | 'real';
  reason: string;
  action: 'rerun_job' | 'rerun_run' | 'fix_turn' | 'wait' | 'blocked_on_human';
  specs: string[];
  logTail: string | null;
  at: string;
}

/** A notification and whether it actually reached the phone (§10). */
export interface Alert {
  id: string;
  key?: string;
  title: string;
  body: string;
  priority: 'low' | 'normal' | 'high';
  category: string;
  link: string | null;
  jobId?: string | null;
  state: 'pending' | 'sent' | 'skipped' | 'failed';
  attempts: number;
  error: string | null;
  at: string;
  sentAt: string | null;
}

/** One (account, model) pair and its measured headroom (§8). */
export interface Pair {
  accountId: string;
  model: string;
  limitedUntil: string | null;
  utilization: number | null;
  resetsAt: string | null;
  sampledAt: string | null;
  enabled: boolean;
  free: boolean;
}

export interface SlotView {
  index: number;
  jobId: string | null;
  title?: string | null;
  ports?: Record<string, number>;
  [k: string]: unknown;
}

export interface CapacityView {
  totalBytes: number;
  reserveBytes: number;
  usableBytes: number;
  measuredPeakBytes: number;
  fullLoadSlots: number;
  configuredSlots: number;
  oversubscribed: boolean;
  note: string;
  slots: SlotView[];
  boxSizeForFour: number;
  measuredFrom: string;
  matrix: Pair[];
  accounts: { id: string; label: string }[];
  models: { id: string; label: string }[];
}

export interface QueueEntry {
  position: number;
  jobId: string;
  title: string;
  repo: string;
  prNumber: number | null;
  state: string;
  detail: string | null;
  enteredAt: string;
  startedAt: string | null;
}

export interface BoardEntry {
  jobId: string;
  title: string;
  branch: string | null;
  lifecycle: string;
  paths: string[];
}

export interface LockView {
  name: string;
  jobId: string;
  title: string;
  takenAt: string;
  note: string | null;
}

export interface LongJobView {
  id: string;
  label: string;
  command: string;
  state: string;
  exitCode: number | null;
  startedAt: string;
  endedAt: string | null;
  [k: string]: unknown;
}

export interface JobActivity {
  state: 'running_tool' | 'thinking' | 'idle';
  tool?: string;
  detail?: string;
  since: string;
}

export interface Account {
  id: string;
  label: string;
  limitedUntil?: string | null;
  running?: number;
  spend30d?: number;
}

export interface ActionResult {
  ok: boolean;
  action: { id: string; kind: string; state: 'pending' | 'done' | 'failed'; error: string | null; receivedAt: string; doneAt: string | null };
  error?: string;
  job?: Job;
}

export interface RepoInfo {
  id: string;
  label: string;
  slug: string;
  defaultBranch: string;
  capabilities: {
    ci: boolean;
    shards: boolean;
    staging: boolean;
    ceremony: boolean;
    pagesDeploy: { project: string; liveUrl: string } | null;
  };
}

export interface Attachment {
  id: string;
  filename: string;
  size: number;
  mime: string | null;
  sha256: string;
  state?: 'stored' | 'verified' | 'delivered' | 'failed';
  error?: string | null;
}

export interface Draft {
  id: string;
  name: string;
  prompt: string;
  templateIds: string[];
  model: string | null;
  effort: string | null;
  mode: string | null;
  kind: 'change' | 'workshop';
  baseBranch: string | null;
  scheduledFor: string | null;
  repo: string;
  referenceRepo: string | null;
  lastActive: boolean;
  updatedAt: string;
  attachments: Attachment[];
}

export interface OpsModel {
  id: string;
  cli: string;
  label: string;
  /** The oldest Claude Code that knows this model. */
  minCli: string;
  efforts: string[];
}

/** What the job asked for against what its session said it ran on. */
export interface ModelCheck {
  picked: { id: string; label: string; cli: string };
  attempt: number | null;
  launched: string | null;
  reported: string | null;
  answered: string | null;
  state: 'not_started' | 'waiting' | 'confirmed' | 'mismatch';
  mismatchedAttempts: number[];
  nextTurn: string | null;
}

export interface Block {
  id: string;
  title: string;
  text: string;
  always: number;
  pin: 'begin' | 'end' | null;
  position: number;
}

export interface Shot {
  id: string;
  head_sha: string | null;
  source: 'self' | 'ci';
  route: string | null;
  width: number | null;
  file: string;
  created_at: string;
  verdict: { verdict?: string; findings?: { rule: string; severity: string; element?: string; message?: string }[] } | null;
}

export interface CiCheck {
  name: string;
  status: string;
  conclusion: string | null;
  started_at?: string | null;
  completed_at?: string | null;
}

export const shotUrl = (jobId: string, shot: Shot): string =>
  `/api/shots/${jobId}/${shot.source}/${shot.source === 'ci' && shot.head_sha ? `${shot.head_sha}/` : ''}${shot.file}`;
